import React, { useState, useEffect } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { usePermission } from '../../context/PermissionContext';
import { getInvoiceConfig, updateInvoiceConfig } from '../../services/api/invoiceConfigApi';
import {
  uploadPrescriptionLogo,
  getLogoPreviewUrl,
} from '../../services/api/prescriptionBrandingApi';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * InvoiceProfileSettings — Organisation Settings → Invoice / Billing profile
 * (T-INV1-FE). Business ruling 4 / story INV-1: legal name, GSTIN,
 * registered address, contact, logo (optional), footer/terms, optional PAN
 * + authorised-signatory label. One profile per org — prints on the
 * invoice (INV-3) and deposit receipt (RCPT-1) headers, with a fallback to
 * the existing clinic header when a field is left blank.
 *
 * Cloned from PrescriptionBrandingSettings.jsx (SectionCard, logo
 * upload/preview, save-via-PUT pattern) per BUILD-PLAN §T-INV1-FE.
 *
 * Logo: reuses the SAME clinic-logo upload/preview machinery as Rx
 * Branding (POST/GET /prescription-logo) — one shared org logo image;
 * the returned s3_key is stored under invoice_config.logo_s3_key.
 *
 * GSTIN: reuses the org's existing `tax_id` (AC3) — prefills from it,
 * writes back to the SAME field on save. No second source of truth.
 *
 * ── Round 3 / S7 — statutory invoice fields (BUILD-PLAN §0b, EP-3) ──────
 * Six additive `invoice_config` keys the renderer (`discharge_documents_
 * pdf.py`) prints: `authorised_signatory_name`, `signatory_designation`,
 * `place_of_supply`, `sac_code`, `declaration`, `eoe_enabled`. Bound
 * VERBATIM to `InvoiceConfigUpdate` (`app/schemas/invoice_config.py`,
 * `extra=forbid`) — do not rename. Signature is WET-INK LINE ONLY
 * (owner-locked, S3 sig-image upload dropped) — no image upload field.
 * `place_of_supply` is NOT prefilled from the org's state here: the
 * renderer already defaults blank → org `state` at print time
 * (`discharge_documents_pdf.py:412`); this form only edits the override,
 * so leaving it blank keeps the single existing default, no second
 * client-side copy of "org state" to fetch/drift.
 * ═══════════════════════════════════════════════════════════════════════
 */

const EMPTY_CONFIG = {
  legal_name: '',
  registered_address: '',
  contact: '',
  logo_s3_key: null,
  footer_terms: '',
  pan: '',
  authorised_signatory_label: '',
  authorised_signatory_name: '',
  signatory_designation: '',
  place_of_supply: '',
  sac_code: '',
  declaration: '',
  eoe_enabled: false,
};

/** Safely extract a renderable string from FastAPI error responses. */
function parseApiError(err, fallback = 'Something went wrong.') {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e.msg || JSON.stringify(e)).join('; ');
  }
  return fallback;
}

export default function InvoiceProfileSettings() {
  const { isDark } = useTheme();
  // AC-NEG: only an org admin can view/edit this section. MANAGE_FEES is the
  // existing ADMIN-only billing permission (no new slug minted — mirrors
  // BUILD-PLAN's backend `_require_admin` gate).
  const canManage = usePermission('MANAGE_FEES');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [gstin, setGstin] = useState('');

  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // ── Load current profile ───────────────────────────────
  useEffect(() => {
    if (!canManage) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await getInvoiceConfig();
        if (cancelled) return;
        const cfg = data?.config || {};
        setConfig({
          legal_name: cfg.legal_name || '',
          registered_address: cfg.registered_address || '',
          contact: cfg.contact || '',
          logo_s3_key: cfg.logo_s3_key || null,
          footer_terms: cfg.footer_terms || '',
          pan: cfg.pan || '',
          authorised_signatory_label: cfg.authorised_signatory_label || '',
          authorised_signatory_name: cfg.authorised_signatory_name || '',
          signatory_designation: cfg.signatory_designation || '',
          place_of_supply: cfg.place_of_supply || '',
          sac_code: cfg.sac_code || '',
          declaration: cfg.declaration || '',
          eoe_enabled: !!cfg.eoe_enabled,
        });
        setGstin(data?.tax_id || '');

        const logoUrl = await getLogoPreviewUrl();
        if (!cancelled && logoUrl) setLogoPreviewUrl(logoUrl);
      } catch (err) {
        // No profile saved yet is a valid empty state, not an error.
        const status = err?.response?.status;
        if (status !== 404 && !cancelled) {
          console.error('[InvoiceProfile] Load failed:', err);
          setError('Failed to load invoice profile.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage]);

  // ── Save ────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const {
        legal_name, registered_address, contact, logo_s3_key,
        footer_terms, pan, authorised_signatory_label,
        authorised_signatory_name, signatory_designation,
        place_of_supply, sac_code, declaration, eoe_enabled,
      } = config;
      const payload = {};
      if (legal_name?.trim()) payload.legal_name = legal_name.trim();
      if (registered_address?.trim()) payload.registered_address = registered_address.trim();
      if (contact?.trim()) payload.contact = contact.trim();
      if (logo_s3_key) payload.logo_s3_key = logo_s3_key;
      if (footer_terms?.trim()) payload.footer_terms = footer_terms.trim();
      if (pan?.trim()) payload.pan = pan.trim();
      if (authorised_signatory_label?.trim()) payload.authorised_signatory_label = authorised_signatory_label.trim();
      // S7 statutory fields (EP-3, InvoiceConfigUpdate — exact key names).
      // Text fields: send only when non-empty (clearing a field removes it
      // from the invoice_config blob on the next save, same pattern as the
      // fields above — the PUT route full-replaces the blob).
      if (authorised_signatory_name?.trim()) payload.authorised_signatory_name = authorised_signatory_name.trim();
      if (signatory_designation?.trim()) payload.signatory_designation = signatory_designation.trim();
      if (place_of_supply?.trim()) payload.place_of_supply = place_of_supply.trim();
      if (sac_code?.trim()) payload.sac_code = sac_code.trim();
      if (declaration?.trim()) payload.declaration = declaration.trim();
      // E&OE is a toggle, not free text — like GSTIN it's the one source of
      // truth for that boolean, so always write it back (including turning
      // it off) rather than silently drop `false`.
      payload.eoe_enabled = !!eoe_enabled;
      // GSTIN is the ONE source of truth (org tax_id) — always write back,
      // including a cleared value, so it never drifts from what's shown here.
      // The PUT body field is `gstin` (the backend write-through strips it out
      // of invoice_config and stamps the org `tax_id`); sending `tax_id`
      // directly 422s on the route's extra=forbid wall (reconcile D).
      payload.gstin = gstin?.trim() || '';

      const result = await updateInvoiceConfig(payload);
      if (result?.config) {
        setConfig((prev) => ({ ...prev, ...result.config }));
      }
      if (typeof result?.tax_id === 'string') {
        setGstin(result.tax_id);
      }
      setSuccess('Invoice profile saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('[InvoiceProfile] Save failed:', err);
      setError(parseApiError(err, 'Failed to save invoice profile.'));
    } finally {
      setSaving(false);
    }
  };

  // ── Logo upload (shared clinic-logo machinery) ──────────
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      setError('Logo must be an image file (PNG or JPG).');
      e.target.value = '';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be under 2MB.');
      e.target.value = '';
      return;
    }
    try {
      setUploadingLogo(true);
      setError(null);
      const result = await uploadPrescriptionLogo(file);
      setConfig((prev) => ({ ...prev, logo_s3_key: result.s3_key }));
      setSuccess('Logo uploaded successfully!');
      setTimeout(() => setSuccess(null), 3000);
      try {
        if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
        const newUrl = await getLogoPreviewUrl();
        setLogoPreviewUrl(newUrl);
      } catch (_previewErr) {
        // Preview refresh failed — logo was uploaded, just not displayable yet.
      }
    } catch (err) {
      setError(parseApiError(err, 'Failed to upload logo.'));
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const removeLogo = () => {
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoPreviewUrl(null);
    setConfig((prev) => ({ ...prev, logo_s3_key: null }));
  };

  // ── Styles ────────────────────────────────────────────
  const cardBg = isDark ? '#16213e' : '#f8fafc';
  const borderColor = isDark ? '#2a2a4a' : '#e2e8f0';
  const textPrimary = isDark ? '#e2e8f0' : '#1a202c';
  const textSecondary = isDark ? '#a0aec0' : '#718096';

  const inputStyle = {
    width: '100%', padding: '8px 12px', fontSize: '13px', border: `1px solid ${borderColor}`,
    borderRadius: '6px', background: cardBg, color: textPrimary, boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: '12px', fontWeight: 600, color: textSecondary, display: 'block', marginBottom: '6px',
  };

  if (!canManage) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: textSecondary }}>
        You don't have permission to view the invoice / billing profile.
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: textSecondary }}>
        Loading invoice profile...
      </div>
    );
  }

  return (
    <div style={{
      padding: '20px',
      maxWidth: '900px',
      margin: '0 auto',
      color: textPrimary,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: textPrimary }}>
          Invoice / Billing Profile
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: '13px', color: textSecondary }}>
          This information prints on your invoices and deposit receipts. Leave a field blank to
          fall back to your existing organisation header.
        </p>
      </div>

      {/* Toast messages */}
      {error && (
        <div style={{
          background: isDark ? '#4a1c1c' : '#fef2f2',
          border: '1px solid #f87171',
          borderRadius: '8px',
          padding: '10px 14px',
          marginBottom: '16px',
          fontSize: '13px',
          color: '#f87171',
        }}>
          {error}
          <button onClick={() => setError(null)} style={{
            float: 'right', background: 'none', border: 'none', color: '#f87171',
            cursor: 'pointer', fontSize: '14px',
          }}>✕</button>
        </div>
      )}
      {success && (
        <div style={{
          background: isDark ? '#1a3a2a' : '#f0fdf4',
          border: '1px solid #4ade80',
          borderRadius: '8px',
          padding: '10px 14px',
          marginBottom: '16px',
          fontSize: '13px',
          color: '#4ade80',
        }}>
          {success}
        </div>
      )}

      {/* ═══ ORGANISATION IDENTITY ═══ */}
      <SectionCard title="Organisation Identity" subtitle="Legal name and tax details as they should appear on invoices" isDark={isDark}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Legal Name</label>
            <input
              type="text"
              placeholder="e.g. Aarogya Health Clinic Pvt. Ltd."
              value={config.legal_name}
              onChange={(e) => setConfig((prev) => ({ ...prev, legal_name: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>GSTIN</label>
            <input
              type="text"
              placeholder="e.g. 27ABCDE1234F1Z5"
              value={gstin}
              onChange={(e) => setGstin(e.target.value)}
              style={inputStyle}
            />
            <span style={{ fontSize: '11px', color: textSecondary, display: 'block', marginTop: '4px' }}>
              Same GSTIN used across your organisation profile.
            </span>
          </div>

          <div>
            <label style={labelStyle}>PAN (optional)</label>
            <input
              type="text"
              placeholder="e.g. ABCDE1234F"
              value={config.pan}
              onChange={(e) => setConfig((prev) => ({ ...prev, pan: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Registered Address</label>
            <textarea
              placeholder="e.g. 42, MG Road, Koregaon Park, Pune, Maharashtra 411001"
              value={config.registered_address}
              onChange={(e) => setConfig((prev) => ({ ...prev, registered_address: e.target.value }))}
              rows={2}
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Contact (phone / email)</label>
            <input
              type="text"
              placeholder="e.g. +91 98765 43210, billing@clinic.in"
              value={config.contact}
              onChange={(e) => setConfig((prev) => ({ ...prev, contact: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Authorised Signatory Label (optional)</label>
            <input
              type="text"
              placeholder="e.g. For Aarogya Health Clinic"
              value={config.authorised_signatory_label}
              onChange={(e) => setConfig((prev) => ({ ...prev, authorised_signatory_label: e.target.value }))}
              style={inputStyle}
            />
          </div>
        </div>
      </SectionCard>

      {/* ═══ LOGO ═══ */}
      <SectionCard title="Logo" subtitle="Optional. Same logo used on prescriptions." isDark={isDark}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {logoPreviewUrl && (
            <img
              src={logoPreviewUrl}
              alt="Organisation logo"
              style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover', border: `1px solid ${borderColor}` }}
            />
          )}
          <label style={{
            padding: '6px 12px', fontSize: '12px', border: `1px solid ${borderColor}`,
            borderRadius: '6px', cursor: 'pointer', color: textSecondary,
            background: cardBg, display: 'inline-block',
          }}>
            {uploadingLogo ? 'Uploading...' : (logoPreviewUrl ? 'Change Logo' : 'Upload Logo')}
            <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} disabled={uploadingLogo} />
          </label>
          {logoPreviewUrl && (
            <button
              onClick={removeLogo}
              style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '12px' }}
            >
              Remove
            </button>
          )}
        </div>
      </SectionCard>

      {/* ═══ FOOTER / TERMS ═══ */}
      <SectionCard title="Footer / Terms" subtitle="Free-text declaration printed at the bottom of invoices and receipts" isDark={isDark}>
        <textarea
          placeholder="e.g. Terms & conditions, refund policy, or a compliance declaration"
          value={config.footer_terms}
          onChange={(e) => setConfig((prev) => ({ ...prev, footer_terms: e.target.value }))}
          rows={3}
          maxLength={1000}
          style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
        />
      </SectionCard>

      {/* ═══ STATUTORY / SIGNATURE DETAILS (S7) ═══ */}
      <SectionCard
        title="Statutory & Signature Details"
        subtitle="Printed on the invoice to meet GST Rule 46 requirements"
        isDark={isDark}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Authorised Signatory Name (optional)</label>
            <input
              type="text"
              placeholder="e.g. Dr. Priya Sharma"
              value={config.authorised_signatory_name}
              onChange={(e) => setConfig((prev) => ({ ...prev, authorised_signatory_name: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Signatory Designation (optional)</label>
            <input
              type="text"
              placeholder="e.g. Medical Director / Proprietor"
              value={config.signatory_designation}
              onChange={(e) => setConfig((prev) => ({ ...prev, signatory_designation: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <span style={{ fontSize: '11px', color: textSecondary, display: 'block' }}>
              A blank line for a wet-ink signature is printed below this name and designation on every
              invoice. Digital signature (DSC) is not supported yet.
            </span>
          </div>

          <div>
            <label style={labelStyle}>Place of Supply</label>
            <input
              type="text"
              placeholder="e.g. Maharashtra"
              value={config.place_of_supply}
              onChange={(e) => setConfig((prev) => ({ ...prev, place_of_supply: e.target.value }))}
              style={inputStyle}
            />
            <span style={{ fontSize: '11px', color: textSecondary, display: 'block', marginTop: '4px' }}>
              Required under GST Rule 46(f). Leave blank to use your organisation's registered state.
            </span>
          </div>

          <div>
            <label style={labelStyle}>SAC Code (taxable services)</label>
            <input
              type="text"
              placeholder="e.g. 9993"
              value={config.sac_code}
              onChange={(e) => setConfig((prev) => ({ ...prev, sac_code: e.target.value }))}
              style={inputStyle}
            />
            <span style={{ fontSize: '11px', color: textSecondary, display: 'block', marginTop: '4px' }}>
              Printed only on taxable invoice lines. Leave blank to omit the SAC column.
            </span>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Declaration (optional)</label>
            <textarea
              placeholder="e.g. Certified that the particulars given above are true and correct."
              value={config.declaration}
              onChange={(e) => setConfig((prev) => ({ ...prev, declaration: e.target.value }))}
              rows={3}
              maxLength={2000}
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600, color: textPrimary,
            }}>
              <input
                type="checkbox"
                checked={config.eoe_enabled}
                onChange={(e) => setConfig((prev) => ({ ...prev, eoe_enabled: e.target.checked }))}
                style={{ width: '16px', height: '16px', accentColor: '#0d9488', cursor: 'pointer' }}
              />
              Print "E. &amp; O.E." on invoice
            </label>
            <span style={{ fontSize: '11px', color: textSecondary, display: 'block', marginTop: '4px', marginLeft: '24px' }}>
              Errors and Omissions Excepted — a standard disclaimer printed at the bottom of the invoice.
            </span>
          </div>
        </div>
      </SectionCard>

      {/* ═══ SAVE ═══ */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 24px',
            fontSize: '14px',
            fontWeight: 600,
            borderRadius: '8px',
            border: 'none',
            background: saving ? '#94a3b8' : 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
            color: '#fff',
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving...' : 'Save Invoice Profile'}
        </button>
      </div>
    </div>
  );
}

/* ── Section Card helper (cloned from PrescriptionBrandingSettings) ──── */
function SectionCard({ title, subtitle, isDark, children }) {
  const cardBg = isDark ? '#16213e' : '#ffffff';
  const borderColor = isDark ? '#2a2a4a' : '#e2e8f0';
  const textPrimary = isDark ? '#e2e8f0' : '#1a202c';
  const textSecondary = isDark ? '#a0aec0' : '#718096';

  return (
    <div style={{
      background: cardBg,
      border: `1px solid ${borderColor}`,
      borderRadius: '12px',
      padding: '18px',
      marginBottom: '16px',
    }}>
      <h3 style={{ margin: '0 0 2px', fontSize: '15px', fontWeight: 700, color: textPrimary }}>{title}</h3>
      {subtitle && <p style={{ margin: '0 0 14px', fontSize: '12px', color: textSecondary }}>{subtitle}</p>}
      {children}
    </div>
  );
}
